import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from '../api/client';

interface AdminBatchSummary {
  org_id: string;
  org_slug: string;
  org_name: string;
  batch_id: string;
  file_count: number;
  total_bytes: number;
  last_upload_at: string | null;
  last_validation_state: string | null;
  last_validated_at: string | null;
}

interface BatchIngestResult {
  ok: boolean;
  titlesCreated: number;
  titlesSkipped: number;
  errors: string[];
}

export function AdminBatchesPage() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<AdminBatchSummary[], ApiError>({
    queryKey: ['admin-batches'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<AdminBatchSummary[]>('/api/v1/admin/batches', { token });
    },
    retry: false,
  });

  if (isLoading) return <div>Loading…</div>;
  if (error) {
    return (
      <div className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        <h1 className="text-xl font-semibold">Couldn't load batches</h1>
        <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">{error.message}</p>
      </div>
    );
  }

  const batches = data ?? [];

  return (
    <div>
      <h1 className="text-2xl font-semibold">Validation queue</h1>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        All batches across all orgs. Validate when a licensor reports their delivery is
        complete (Submit Upload triggers an email; uploads keep streaming until then).
      </p>

      {batches.length === 0 ? (
        <div className="mt-8 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6 text-sm text-[color:var(--color-text-secondary)]">
          No batches uploaded yet.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded border border-[color:var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-bg-card)] text-left text-xs uppercase tracking-wider text-[color:var(--color-text-muted)]">
              <tr>
                <th className="px-4 py-2">Org</th>
                <th className="px-4 py-2">Batch</th>
                <th className="px-4 py-2">Files</th>
                <th className="px-4 py-2">Last upload</th>
                <th className="px-4 py-2">Last validation</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <BatchRow
                  key={`${b.org_id}::${b.batch_id}`}
                  batch={b}
                  onValidated={() => queryClient.invalidateQueries({ queryKey: ['admin-batches'] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BatchRow({
  batch,
  onValidated,
}: {
  batch: AdminBatchSummary;
  onValidated: () => void;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState<BatchIngestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function validate() {
    setError(null);
    setResult(null);
    setValidating(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await apiRequest<BatchIngestResult>(
        `/api/v1/admin/batches/${encodeURIComponent(batch.org_id)}/${encodeURIComponent(batch.batch_id)}/validate`,
        { token, method: 'POST' }
      );
      setResult(resp);
      onValidated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setValidating(false);
    }
  }

  return (
    <>
      <tr className="border-t border-[color:var(--color-border)]">
        <td className="px-4 py-3">
          <div>{batch.org_name}</div>
          <div className="text-xs text-[color:var(--color-text-secondary)] font-mono">
            {batch.org_slug}
          </div>
        </td>
        <td className="px-4 py-3 font-mono">{batch.batch_id}</td>
        <td className="px-4 py-3">{batch.file_count}</td>
        <td className="px-4 py-3 text-[color:var(--color-text-secondary)]">
          {batch.last_upload_at ? new Date(batch.last_upload_at).toLocaleString() : '—'}
        </td>
        <td className="px-4 py-3">
          {batch.last_validated_at ? (
            <ValidationBadge state={batch.last_validation_state} at={batch.last_validated_at} />
          ) : (
            <span className="text-[color:var(--color-text-secondary)]">never</span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          <button
            type="button"
            onClick={validate}
            disabled={validating}
            className="rounded bg-[color:var(--color-crimson)] px-3 py-1 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {validating ? 'Validating…' : 'Validate'}
          </button>
        </td>
      </tr>
      {(result || error) && (
        <tr>
          <td colSpan={6} className="border-t border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] px-4 py-3">
            {error ? (
              <p className="text-sm text-[color:var(--color-warning)]">{error}</p>
            ) : result ? (
              <ValidationResult result={result} />
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

function ValidationBadge({ state, at }: { state: string | null; at: string }) {
  const label = state ?? 'unknown';
  const color =
    state === 'passed'
      ? 'var(--color-success, #4ade80)'
      : state === 'failed'
      ? 'var(--color-warning)'
      : 'var(--color-text-secondary)';
  return (
    <span style={{ color }} title={new Date(at).toLocaleString()}>
      {label}
    </span>
  );
}

function ValidationResult({ result }: { result: BatchIngestResult }) {
  const status = result.ok ? 'Passed' : 'Failed';
  const color = result.ok ? 'var(--color-success, #4ade80)' : 'var(--color-warning)';
  return (
    <div className="text-sm">
      <div className="flex items-baseline justify-between">
        <span className="font-semibold" style={{ color }}>{status}</span>
        <span className="text-xs text-[color:var(--color-text-secondary)]">
          {result.titlesCreated} created · {result.titlesSkipped} updated
        </span>
      </div>
      {result.errors.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-[color:var(--color-text-secondary)]">
          {result.errors.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
